import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Skeleton,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Switch,
  FormControlLabel
} from '@mui/material';
import { toast } from 'react-toastify';
import palette from 'src/theme/palette';
import Page from 'src/components/Page';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EmailTemplateModal from './EmailTemplateModal';
import EmailTemplateViewModal from './EmailTemplateViewModal';
import { useTheme } from '@mui/material/styles';
import {
  getEmailTemplate,
  deleteEmailTemplate,
  chnageEmailStatus,
  getFormTypes
} from 'src/ApiServices';

export default function EmailTemplates() {
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatesData, setTemplatesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formTypes, setFormTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusType, setStatusType] = useState('all');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1,
    has_more_pages: false
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const theme = useTheme();

  // Fetch templates with search and filter
  const fetchTemplatesData = async (currentPage = 1, filters = {}) => {
    try {
      setLoading(true);

      // Prepare API parameters
      const params = {
        page: currentPage,
        ...(filters.search && { search: filters.search }),
        ...(filters.formType && filters.formType !== 'all' && { form_type: filters.formType })
      };

      const response = await getEmailTemplate(params);
      const transformedData = transformApiData(response.data || []);
      setTemplatesData(transformedData);
      setPagination(
        response.pagination || {
          current_page: 1,
          per_page: 20,
          total: response.data?.length || 0,
          last_page: 1,
          has_more_pages: false
        }
      );
    } catch (error) {
      console.error('Error fetching email templates:', error);
      setTemplatesData([]);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  // Handle search submit
  const handleSearchSubmit = async () => {
    setPage(1);
    const searchFilters = {
      ...appliedFilters,
      ...(searchQuery && { search: searchQuery }),
      formType: statusType
    };
    setAppliedFilters(searchFilters);
    await fetchTemplatesData(1, searchFilters);
  };

  // Handle clear search
  const handleClearSearch = async () => {
    setSearchQuery('');
    setPage(1);
    const filtersWithoutSearch = { ...appliedFilters };
    delete filtersWithoutSearch.search;
    setAppliedFilters(filtersWithoutSearch);
    await fetchTemplatesData(1, { ...filtersWithoutSearch, formType: statusType });
  };

  // Transform API data
  const transformApiData = (apiData) => {
    return apiData
      .map((template) => {
        let formTypeValue = 'N/A';
        let formTypeName = 'N/A';
        let formTypeId = null;

        // Handle form_type field
        if (template.form_type) {
          if (typeof template.form_type === 'object') {
            formTypeValue = template.form_type.id || template.form_type.name || 'N/A';
            formTypeName = template.form_type.name || 'N/A';
            formTypeId = template.form_type.id;
          } else {
            formTypeValue = template.form_type;
            formTypeName = template.form_type;
          }
        } else if (template.type_id) {
          formTypeValue = template.type_id;
          formTypeId = template.type_id;
        }

        if (formTypeName === 'N/A' && formTypeId && formTypes.length > 0) {
          const foundType = formTypes.find((type) => type.id === formTypeId);
          if (foundType) {
            formTypeName = foundType.name;
          }
        }

        return {
          id: template.id,
          name: template.name || 'Unnamed Template',
          subject: template.subject || 'No Subject',
          form_type: formTypeValue,
          form_type_name: formTypeName,
          status: template.status || 'inactive',
          body: template.body,
          success: template.success,
          error: template.error,
          created_at: template.created_at,
          updated_at: template.updated_at,
          originalFormType: template.form_type,
          type_id: template.type_id || formTypeId
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
  };

  useEffect(() => {
    fetchTemplatesData(page, { ...appliedFilters, formType: statusType });
  }, [page, statusType]);

  useEffect(() => {
    fetchFormTypes();
  }, []);

  const fetchFormTypes = async () => {
    try {
      const response = await getFormTypes();
      console.log('Email Template Form Types API Response:', response);

      if (response.data && Array.isArray(response.data)) {
        setFormTypes(response.data);
      } else {
        setFormTypes([]);
        toast.warning('No form types available');
      }
    } catch (error) {
      console.error('Error fetching form types:', error);
      setFormTypes([]);
      toast.error('Failed to load form types');
    }
  };

  const handleAddClick = () => {
    setSelectedTemplate(null);
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleEditClick = (template) => {
    // Ensure template has the form_type_id for the modal
    const templateWithFormId = {
      ...template,
      form_type_id: template.type_id || template.form_type_id
    };
    setSelectedTemplate(templateWithFormId);
    setIsEditing(true);
    setModalOpen(true);
  };
  const handleViewClick = (template) => {
    setSelectedTemplate(template);
    setViewModalOpen(true);
  };

  const handleDeleteClick = (template) => {
    setSelectedTemplate(template);
    setDeleteModalOpen(true);
  };

  const handleStatusChange = async (template, newStatus) => {
    try {
      const updatedTemplates = templatesData.map((t) =>
        t.id === template.id ? { ...t, status: newStatus } : t
      );
      setTemplatesData(updatedTemplates);

      let formTypeValue = null;

      if (template.originalFormType) {
        if (typeof template.originalFormType === 'object') {
          formTypeValue = template.originalFormType.id;
        } else {
          formTypeValue = template.originalFormType;
        }
      } else if (template.type_id) {
        formTypeValue = template.type_id;
      } else if (template.form_type && template.form_type !== 'N/A') {
        formTypeValue = template.form_type;
      }

      const payload = {
        status: newStatus === 'active' ? 2 : 15
      };

      if (formTypeValue) {
        payload.form_type = formTypeValue;
      }

      console.log('Sending status change request with payload:', payload);

      // Make API call
      await chnageEmailStatus(template.id, payload);

      toast.success(
        `Template ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`
      );
    } catch (error) {
      console.error('Failed to update template status:', error);

      const revertedTemplates = templatesData.map((t) =>
        t.id === template.id ? { ...t, status: template.status } : t
      );
      setTemplatesData(revertedTemplates);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to update template status!';

      toast.error(errorMessage);
    }
  };

  const handleTypeChange = (event) => {
    setStatusType(event.target.value);
    setPage(1);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTemplate) return;
    try {
      setDeleteLoading(true);
      await deleteEmailTemplate(selectedTemplate.id);
      toast.success('Email template deleted successfully!');
      fetchTemplatesData(page, { ...appliedFilters, formType: statusType });
      setDeleteModalOpen(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error(error.response?.data?.message || 'Failed to delete template!');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleModalSubmit = async (formData) => {
    try {
      toast.success(isEditing ? 'Template updated successfully!' : 'Template added successfully!');
      fetchTemplatesData(page, { ...appliedFilters, formType: statusType });
      setModalOpen(false);
    } catch (error) {
      console.error('Error saving template:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors ||
        `Failed to ${isEditing ? 'update' : 'add'} template`;
      toast.error(errorMessage);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const tableHeaders = ['Name', 'Form Type', 'Status', 'Change Status', 'Actions'];

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={index}>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={120} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={100} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={80} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2 }}>
          <Skeleton variant="text" width={80} height={25} />
        </TableCell>
        <TableCell sx={{ px: 2, textAlign: 'center' }}>
          <Skeleton variant="circular" width={40} height={40} />
        </TableCell>
      </TableRow>
    ));

  const renderNoRecordsFound = () => (
    <TableRow>
      <TableCell
        colSpan={tableHeaders.length}
        sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{ color: palette.text.secondary, fontFamily: 'poppinsMedium', mb: 1 }}
          >
            No Email Templates Found
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: palette.grey[600],
              fontFamily: 'poppinsRegular',
              maxWidth: 400,
              textAlign: 'center'
            }}
          >
            {Object.keys(appliedFilters).length > 0 || searchQuery
              ? 'No email templates match your current filters. Try adjusting your search criteria.'
              : 'No email templates available. Add new templates to get started.'}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Page title={`Email Templates | ${process.env.REACT_APP_PROJECTNAME}`}>
      <Box sx={{ p: { xs: 0, sm: 2, md: 3 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 2,
            pb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 20, md: 25 },
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark
            }}
          >
            Email Templates
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              minWidth: 180,
              '&:hover': { background: palette.primary.dark }
            }}
          >
            Add Email Template
          </Button>
        </Box>

        {/* Search Section - Updated with Clear Icon */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={7} lg={8}>
            <TextField
              variant="outlined"
              placeholder="Search by template name..."
              size="medium"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: palette.text.secondary }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2} lg={2}>
            <FormControl fullWidth size="medium">
              <InputLabel id="form-type-label">Form Type</InputLabel>
              <Select
                labelId="form-type-label"
                value={statusType}
                label="Form Type"
                onChange={handleTypeChange}
                sx={{
                  fontFamily: 'poppinsMedium',
                  backgroundColor: theme.palette.background.paper
                }}
              >
                <MenuItem value="all">All Form Types</MenuItem>
                {console.log('Form Types:', formTypes)}
                {formTypes.map((formType) => (
                  <MenuItem key={formType.id} value={formType.id}>
                    {formType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <Button
              variant="contained"
              onClick={handleSearchSubmit}
              fullWidth
              sx={{
                height: 56,
                fontSize: '16px',
                fontFamily: 'poppinsMedium'
              }}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        {/* Table */}
        <Card>
          <TableContainer
            sx={{
              overflowX: 'auto',
              width: '100%'
            }}
          >
            <Table
              sx={{
                minWidth: 1400,
                '& .MuiTableCell-head': {
                  backgroundColor: palette.background.paper,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsSemiBold',
                  fontSize: '14px',
                  borderBottom: `2px solid ${palette.divider}`,
                  py: 2
                },
                '& .MuiTableCell-body': {
                  color: palette.text.primary,
                  fontFamily: 'poppinsRegular',
                  fontSize: '14px',
                  borderBottom: `1px solid ${palette.divider}`,
                  py: 2
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header, index) => (
                    <TableCell key={index} sx={{ px: 2, whiteSpace: 'nowrap' }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? renderSkeletonRows()
                  : templatesData.length > 0
                  ? templatesData.map((template) => (
                      <TableRow
                        key={template.id}
                        sx={{ '&:hover': { backgroundColor: palette.action.hover } }}
                      >
                        <TableCell sx={{ px: 2 }}>{template.name}</TableCell>
                        <TableCell sx={{ px: 2 }}>{template?.form_type_name || 'N/A'}</TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                template.status === 'active'
                                  ? palette.success.main
                                  : palette.error.main,
                              fontFamily: 'poppinsMedium'
                            }}
                          >
                            {template.status === 'active' ? 'Active' : 'Inactive'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 2 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={template.status === 'active'}
                                onChange={(e) =>
                                  handleStatusChange(
                                    template,
                                    e.target.checked ? 'active' : 'inactive'
                                  )
                                }
                                color="primary"
                              />
                            }
                            label=""
                          />
                        </TableCell>
                        <TableCell sx={{ px: 2, textAlign: 'start' }}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleViewClick(template)}
                              sx={{ color: palette.info.main, mr: 1 }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(template)}
                              sx={{ color: palette.primary.main, mr: 1 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(template)}
                              sx={{ color: palette.error.main }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  : renderNoRecordsFound()}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Pagination */}
        {!loading && templatesData.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}

        {console.log('Templates Data:', templatesData)}
        {/* Add/Edit Modal */}
        <EmailTemplateModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleModalSubmit}
          template={selectedTemplate}
          isEditing={isEditing}
        />

        {/* View Modal */}
        <EmailTemplateViewModal
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          template={selectedTemplate}
        />

        {/* Delete Confirmation Modal */}
        <Dialog
          open={deleteModalOpen}
          onClose={handleDeleteCancel}
          PaperProps={{ sx: { borderRadius: 2, padding: 1 } }}
        >
          <DialogTitle sx={{ textAlign: 'center', py: 3 }}>
            <WarningAmberIcon sx={{ fontSize: 60, color: palette.warning.main, mb: 2 }} />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'poppinsSemiBold', color: palette.text.primary }}
            >
              Confirm Deletion
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
            <DialogContentText sx={{ color: palette.text.secondary, fontFamily: 'poppinsRegular' }}>
              Are you sure you want to delete the template "{selectedTemplate?.name}"?
              <br />
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
            <Button
              onClick={handleDeleteCancel}
              variant="outlined"
              sx={{
                borderColor: palette.grey[400],
                color: palette.text.primary,
                minWidth: 100
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              sx={{
                backgroundColor: palette.error.main,
                color: palette.error.contrastText,
                minWidth: 100,
                '&:hover': { backgroundColor: palette.error.dark }
              }}
              disabled={deleteLoading}
              startIcon={<DeleteIcon />}
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
